/* eslint-disable no-unused-vars */
import {
  PageState,
  LikeRequest,
  DeleteRequest,
  EditRequest,
  UploadResponse,
  ResourceRequest,
  ResourcesPage,
  ResourceName,
  OnlyId,
  Resource,
  OkResponse,
} from 'store/types';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import { resourcesAPI } from 'api/Api';
import initLikeReducers from './extraReducers/likes';
import initDeleteReducers from './extraReducers/delete';
import initEditReducers from './extraReducers/edit';
import { DESKTOP_IMAGES_LIMIT } from 'utils/utils';

export const getResourceList = createAsyncThunk<
  ResourcesPage,
  ResourceRequest,
  { rejectValue: boolean }
>('page/getResourceList', async (args, { rejectWithValue }) => {
  try {
    const response = await resourcesAPI.getResourceList(args);

    return response;
  } catch (err) {
    return rejectWithValue(false);
  }
});

export const getResourceById = createAsyncThunk<
  Resource,
  OnlyId,
  { rejectValue: boolean }
>('page/getResourceById', async (arg, { rejectWithValue }) => {
  try {
    const response = await resourcesAPI.getById(arg);

    return response;
  } catch (err) {
    return rejectWithValue(false);
  }
});

export const likeRequest = createAsyncThunk<
  Resource,
  LikeRequest,
  { rejectValue: boolean }
>('page/likeRequest', async (args, { rejectWithValue }) => {
  try {
    const response = await resourcesAPI.like(args);

    return response;
  } catch (err) {
    return rejectWithValue(false);
  }
});

export const deleteRequest = createAsyncThunk<
  OkResponse,
  DeleteRequest,
  { rejectValue: boolean }
>('page/deleteRequest', async (arg, { rejectWithValue }) => {
  try {
    const response = resourcesAPI.delete(arg);

    return response;
  } catch (err) {
    return rejectWithValue(false);
  }
});

export const editRequest = createAsyncThunk<
  Resource,
  EditRequest,
  { rejectValue: boolean }
>('page/editRequest', async (args, { rejectWithValue }) => {
  try {
    const response = await resourcesAPI.edit(args);

    return response;
  } catch (err) {
    return rejectWithValue(false);
  }
});

export const uploadRequest = createAsyncThunk<
  UploadResponse,
  Promise<UploadResponse>,
  { rejectValue: boolean }
>('page/uploadRequest', async (args, { rejectWithValue }) => {
  try {
    const response = await args;

    return response;
  } catch (err) {
    return rejectWithValue(false);
  }
});

const initialState: PageState = {
  globalLoadingCounter: 0,
  isLikePending: false,
  totalPages: 0,
  imagesLimit: DESKTOP_IMAGES_LIMIT,
  result: null,
  isResultFrozen: false,
  categories: {
    'Фотографии': {
      id: 'Фотографии',
      href: '/photos',
    },
    'Векторная графика': {
      id: 'Векторная графика',
      href: '/vectors',
    },
    'Иллюстрации': {
      id: 'Иллюстрации',
      href: '/illustrations',
    },
  },
  resources: {},
  lastFetchedItem: null,
};

export const pageSlice = createSlice({
  name: 'page',
  initialState,
  reducers: {
    dropResult: (state) => {
      state.result = null;
    },
    setLastFetchedItem: (state, { payload }) => {
      state.lastFetchedItem = payload;
    },
    setGlobalLoadingCounter: (state, { payload }) => {
      state.globalLoadingCounter = payload;
    },
    setImagesLimit: (state, { payload }) => {
      state.imagesLimit = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(uploadRequest.pending, (state) => {
      state.result = null;
    });

    builder.addCase(getResourceList.pending, (state) => {
      state.result = null;
      state.globalLoadingCounter += 1;
    });
    builder.addCase(getResourceList.fulfilled, (state, { payload, meta }) => {
      const resourceName = meta.arg._saveName;
      state.resources[resourceName] = payload;
      state.globalLoadingCounter -= 1;
    });
    builder.addCase(getResourceList.rejected, (state) => {
      state.result = {
        type: 'error',
        message: 'Упс, кажется, что-то пошло не так',
      };
      state.globalLoadingCounter -= 1;
    });

    builder.addCase(getResourceById.pending, (state) => {
      state.globalLoadingCounter += 1;
      state.lastFetchedItem = null;
    });
    builder.addCase(getResourceById.fulfilled, (state, { payload }) => {
      state.globalLoadingCounter -= 1;
      state.lastFetchedItem = payload;
    });
    builder.addCase(getResourceById.rejected, (state) => {
      state.globalLoadingCounter -= 1;
    });

    initDeleteReducers(builder, deleteRequest);
    initLikeReducers(builder, likeRequest);
    initEditReducers(builder, editRequest);
  },
});

export const {
  dropResult,
  setLastFetchedItem,
  setGlobalLoadingCounter,
  setImagesLimit,
} = pageSlice.actions;

// Общие для всех страниц селекторы
export const selectPageIsLoading = (state: RootState) =>
  state.page.globalLoadingCounter > 0 ||
  state.auth.globalLoadingCounter > 0 ||
  state.admin.globalLoadingCounter > 0;
export const selectTotalPages = (state: RootState) => state.page.totalPages;
export const selectResult = (state: RootState) => state.page.result;

export const selectIsLikePending = (state: RootState) =>
  state.page.isLikePending;
export const selectPendingLikeId = (state: RootState) =>
  state.page.pendingLikeId;
export const selectImagesLimit = (state: RootState) => state.page.imagesLimit;

export const selectCategories = (state: RootState) => state.page.categories;

// Страница cabinet
export const selectResourceByName = (name: ResourceName, state: RootState) =>
  state.page.resources[name];

// Страница card
export const selectCardData = (state: RootState) => state.page.lastFetchedItem;

export default pageSlice.reducer;
